import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Eye, MessageSquare, Edit, Trash2, RefreshCw, TrendingUp, Loader2, Plus, BarChart3 } from 'lucide-react';
import AIInsightsCard from '../components/AIInsightsCard';
import TrustScoreCard from '../components/TrustScoreCard';
import { useCountry } from '../context/CountryContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Dashboard = () => {
  const { formatCurrency } = useCountry();
  const navigate = useNavigate();
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [user, setUser] = useState(null);

  useEffect(() => {
    // Check if user is logged in
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login', { state: { from: '/dashboard', message: 'Please login to view your dashboard' } });
      return;
    }
    try {
      const stored = localStorage.getItem('user');
      if (stored) setUser(JSON.parse(stored));
    } catch (e) { /* ignore */ }
    fetchMyListings();
  }, [navigate]);

  const fetchMyListings = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${API_URL}/api/listings/my-listings`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const data = await response.json();
      
      if (data.success) {
        setAds(data.listings);
      } else {
        setError(data.detail || 'Failed to fetch listings');
      }
    } catch (err) {
      console.error('Error fetching listings:', err);
      setError('Failed to fetch your listings');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this ad?')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/listings/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const data = await response.json();
      
      if (data.success) {
        setAds(ads.filter(ad => ad.id !== id));
      } else {
        alert(data.detail || 'Failed to delete ad');
      }
    } catch (err) {
      console.error('Error deleting ad:', err);
      alert('Failed to delete ad');
    }
  };

  const handleRefresh = (id) => {
    alert('Ad refreshed! It will appear at the top.');
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const AdCard = ({ ad }) => (
    <Card className="hover:shadow-lg transition-shadow">
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div className="flex gap-4 flex-1">
            {/* Ad Image */}
            {ad.images && ad.images.length > 0 && (
              <div className="w-24 h-24 rounded-lg overflow-hidden flex-shrink-0">
                <img 
                  src={ad.images[0].startsWith('http') ? ad.images[0] : `${API_URL}${ad.images[0]}`} 
                  alt={ad.title}
                  className="w-full h-full object-cover"
                  onError={(e) => { e.target.src = 'https://via.placeholder.com/96?text=No+Image'; }}
                />
              </div>
            )}
            <div className="flex-1">
              <div className="flex items-center space-x-2 mb-2">
                <Link to={`/listing/${ad.id}`} className="hover:text-blue-600">
                  <h3 className="font-semibold text-lg">{ad.title}</h3>
                </Link>
                <Badge variant={ad.status === 'active' ? 'default' : 'secondary'}>
                  {ad.status}
                </Badge>
                {ad.featured && (
                  <Badge className="bg-yellow-500">Featured</Badge>
                )}
              </div>
              <p className="text-2xl font-bold text-blue-600 mb-3">{formatCurrency(ad.price)}</p>
              <div className="flex items-center space-x-4 text-sm text-gray-500 mb-4">
                <div className="flex items-center">
                  <Eye className="h-4 w-4 mr-1" />
                  <span>{ad.views || 0} views</span>
                </div>
                <div className="flex items-center">
                  <MessageSquare className="h-4 w-4 mr-1" />
                  <span>{ad.messages || 0} messages</span>
                </div>
              </div>
              <div className="text-sm text-gray-500">
                <p>Posted: {formatDate(ad.created_at)}</p>
                {ad.expires_at && <p>Expires: {formatDate(ad.expires_at)}</p>}
              </div>
            </div>
          </div>
          <div className="flex flex-col space-y-2 ml-4">
            <Button size="sm" variant="outline" onClick={() => navigate(`/edit-ad/${ad.id}`)}>
              <Edit className="h-4 w-4 mr-1" />
              Edit
            </Button>
            <Button size="sm" variant="outline" onClick={() => navigate(`/listing/${ad.id}`)}>
              <Eye className="h-4 w-4 mr-1" />
              View
            </Button>
            <Button size="sm" variant="outline" onClick={() => handleRefresh(ad.id)}>
              <RefreshCw className="h-4 w-4 mr-1" />
              Refresh
            </Button>
            <Button size="sm" variant="outline" className="text-red-600 hover:text-red-700" onClick={() => handleDelete(ad.id)}>
              <Trash2 className="h-4 w-4 mr-1" />
              Delete
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  const activeAds = ads.filter(ad => ad.status === 'active');
  const expiredAds = ads.filter(ad => ad.status === 'expired');

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="dashboard-page">
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white py-12">
        <div className="max-w-7xl mx-auto px-4">
          <h1 className="text-4xl font-bold mb-2" data-testid="user-greeting">
            {user?.name ? `Welcome back, ${user.name.split(' ')[0]}` : 'Welcome to your Dashboard'}
          </h1>
          <p className="text-blue-100">Manage your ads and account</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6">
            {error}
          </div>
        )}

        {/* Trust Intelligence - score, badge, and improvement hints */}
        <div className="mb-8">
          <TrustScoreCard />
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 mb-1">Active Ads</p>
                  <p className="text-3xl font-bold text-blue-600">{activeAds.length}</p>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <TrendingUp className="h-6 w-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 mb-1">Total Views</p>
                  <p className="text-3xl font-bold text-green-600">
                    {ads.reduce((sum, ad) => sum + (ad.views || 0), 0)}
                  </p>
                </div>
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                  <Eye className="h-6 w-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 mb-1">Messages</p>
                  <p className="text-3xl font-bold text-purple-600">
                    {ads.reduce((sum, ad) => sum + (ad.messages || 0), 0)}
                  </p>
                </div>
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                  <MessageSquare className="h-6 w-6 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 mb-1">Expired Ads</p>
                  <p className="text-3xl font-bold text-gray-600">{expiredAds.length}</p>
                </div>
                <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center">
                  <RefreshCw className="h-6 w-6 text-gray-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* AI Insights Summary + Analytics Link */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          <div className="lg:col-span-2">
            <AIInsightsCard variant="summary" maxInsights={3} />
          </div>
          <Card className="bg-gradient-to-br from-blue-50 to-purple-50 border-blue-200">
            <CardContent className="p-6 flex flex-col items-center justify-center h-full text-center">
              <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center mb-4">
                <BarChart3 className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">View Full Analytics</h3>
              <p className="text-sm text-gray-600 mb-4">
                Get detailed insights, charts, and performance metrics for all your ads
              </p>
              <Link to="/analytics">
                <Button className="bg-gradient-to-r from-blue-600 to-purple-600">
                  Open Analytics Dashboard
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>

        {/* Ads List */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>My Ads</CardTitle>
              <Link to="/post-ad">
                <Button className="bg-gradient-to-r from-blue-600 to-purple-600">
                  <Plus className="h-4 w-4 mr-2" />
                  Post New Ad
                </Button>
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="active">
              <TabsList className="mb-6">
                <TabsTrigger value="active">Active ({activeAds.length})</TabsTrigger>
                <TabsTrigger value="expired">Expired ({expiredAds.length})</TabsTrigger>
                <TabsTrigger value="all">All ({ads.length})</TabsTrigger>
              </TabsList>
              <TabsContent value="active" className="space-y-4">
                {activeAds.length > 0 ? (
                  activeAds.map(ad => <AdCard key={ad.id} ad={ad} />)
                ) : (
                  <div className="text-center py-12">
                    <p className="text-gray-500 mb-4">You don't have any active ads</p>
                    <Link to="/post-ad">
                      <Button>Post Your First Ad</Button>
                    </Link>
                  </div>
                )}
              </TabsContent>
              <TabsContent value="expired" className="space-y-4">
                {expiredAds.length > 0 ? (
                  expiredAds.map(ad => <AdCard key={ad.id} ad={ad} />)
                ) : (
                  <div className="text-center py-12">
                    <p className="text-gray-500">No expired ads</p>
                  </div>
                )}
              </TabsContent>
              <TabsContent value="all" className="space-y-4">
                {ads.length > 0 ? (
                  ads.map(ad => <AdCard key={ad.id} ad={ad} />)
                ) : (
                  <div className="text-center py-12">
                    <p className="text-gray-500 mb-4">You haven't posted any ads yet</p>
                    <Link to="/post-ad">
                      <Button>Post Your First Ad</Button>
                    </Link>
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;
